/**
 * @module app.um
 */

import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as FM from 'app/fm/models/essette-api';
import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AuthService } from 'app/um/services/auth-service';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { validate } from 'app/shared/elements/ess-rule';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { TickService } from 'app/shared/services/event-services';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/essette-api';
import { IApiActionResults } from 'app/shared/lib/api/api-types';
import { DocumentsService } from 'app/core/services/documents-service';
import { I18N } from 'aurelia-i18n';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { MemberPlanChangeEvent } from 'app/cd/lib/events';

@autoinject()
export class CreateAuthForm extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable
    public memberPlan: CD.MemberPlan | null = null;

    @bindable
    public taskId: string = '';

    @bindable.stringNull({ defaultBindingMode: bindingMode.twoWay })
    public authId: string | null = null;

    @bindable.objectNull
    public faxDocument: FM.FaxDocument | null = null;

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        addTargetProperty: (document: UM.AuthDocument) => { document.auth = { id: this.authId } }
    };

    private auth: UM.Auth = {};

    public authCreated: boolean = false;

    protected users: UM.AuthOwner[] = [];
    protected authClasses: UM.AuthClass[] = [];
    protected authSubClasses: UM.AuthSubClass[] = [];
    protected authTypes: UM.AuthType[] = [];
    protected authPriorities: UM.AuthPriority[] = [];
    protected reasons: UM.AuthRequestedOverrideReason[] = [];

    private authMemberChanged: Subscription | null = null;

    protected overrideRequestedDate: boolean = false;
    protected canOverrideRequestedDate: boolean = false;

    protected assignedToInvalid: boolean | null = null;

    protected hideOtherReferenceNumberField = false;

    protected validateForm = validate;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly documentService: DocumentsService,
        private readonly toaster: Toaster,
        protected transitional: TransitionalManager,
        protected readonly ticker: TickService,
        private readonly i18n: I18N,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected async bind(): Promise<void> {
        await this.loadLookups();
        await this.checkPermissions();

        if(this.auth) {
            this.auth.requestedDateTime = this.env.now();

            if(!!this.auth.assignedTo) {
                await this.verifyAssignment();
            }
        }

        this.authMemberChanged = this.events.subscribe(MemberPlanChangeEvent, async (event: MemberPlanChangeEvent) => {
            await this.verifyAssignment(event.memberPlan);
        });

        this.documentService.configure(this.documentsConfig);
    }

    protected deactivate(): void {
        this.reset();
    }

    protected detached(): void {
        if(this.authMemberChanged) {
            this.authMemberChanged.dispose();
        }
    }

    ////****************** Events ***************************************************////

    protected async assignedToChanged(): Promise<void> {
        await this.verifyAssignment();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canOverrideRequestedDate = this.accountContext.hasPermission('UM.Auth.Duty.OverrideRequestedDate');
        this.hideOtherReferenceNumberField = this.env.config.pageConfigurations?.um?.auth?.manageAuths?.create?.hideOtherReferenceNumberField || false;
    }

    protected async verifyAssignment(newMemberPlan?: CD.MemberPlan): Promise<void> {
        const memberId = newMemberPlan ? newMemberPlan.member?.id : this.memberPlan?.member?.id;
        const assignedToId = this.auth.assignedTo?.id;

        if(!memberId || !assignedToId) {
            this.assignedToInvalid = null;
            return;
        }

        try {
            this.state.beginProcessing();

            const response = await this.authService.verifyAssignmentByMember(assignedToId, memberId);

            // make sure "assignedTo" nor memberId hasn't changed while awaiting response
            if(this.auth.assignedTo?.id !== assignedToId || (!newMemberPlan && this.memberPlan?.member?.id !== memberId)) {
                this.logger.info(this.i18n.tr('ui:messages.warnings.verificationInterruptionDiscardResults'));
            }
            else {
                this.assignedToInvalid = null;
                if(response.isSuccessful && response.result.filter && response.result.filter.items) {
                    // if no items come back, assigned to is invalid
                    this.assignedToInvalid = (response.result.filter.items.length === 0);
                }
            }
        } finally {
            this.state.endProcessing();
        }
    }

    /**
     * Validate override date
     * @param value the specified override date
     * @returns true/false
     */
    protected isValidOverrideDate(value: string): boolean {
        if(!value) { return true; }

        // check that the override date is not in the future
        return new DateTimeValue(this.env.now()).isAfter(new DateTimeValue(value));
    }

    private async loadLookups(): Promise<void> {
        const options: StandardLoadOptions = {
            all: true
        };
        try {
            this.state.beginLoad();
            const [users, classes, types, priorities, reasons] = await Promise.all([
                this.authService.loadAuthOwners(options),
                this.authService.loadAuthClasses(options, ['subClasses']),
                this.authService.loadAuthTypes(true, options), //load all auth types, even IP only ones
                this.authService.loadAuthPriorities(options),
                this.authService.loadAuthRequestedOverrideReasons()
            ]);

            this.users = users.results;
            this.authClasses = classes.results;
            this.authTypes = types.results;
            this.authPriorities = priorities.results;
            // set default selected Priority
            if(!this.auth.priority) {
                // If faxdocument is to be attached, then the default auth priority will be set to the fax document's priority by default. If not, the the default will be set to Standard
                this.auth.priority = this.faxDocument?.priority?.code ? this.authPriorities.find(ap => ap.code === this.faxDocument?.priority?.code) : this.authPriorities.find(ap => ap.description === 'Standard');
            }

            // Set request date to fax received date
            if(this.faxDocument)
                this.auth.requestedDateTime = this.faxDocument?.receivedTimestamp;


            // check if queue is configured for a specific auth class
            if(this.faxDocument?.queue?.actions?.some(a => a.code == 'UM.Auth.Attach')) {
                const foundParameter = this.faxDocument.queue?.parameters?.find(p => p.description == 'Auth Class');
                if(foundParameter) {
                    const authClassId = foundParameter.argument;
                    this.auth.class = this.authClasses.find(ac => ac.id == authClassId);
                }
            }
            this.reasons = reasons.results;
        } catch(error) {
            this.logger.error('Error loading lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async createAuth(newAuth: UM.Auth): Promise<IApiActionResults> {
        try {
            this.state.beginSave();

            const response = await this.authService.insertAuth(newAuth);
            this.toaster.standard.created('um:auth.$', response.isSuccessful);
            this.auth.id = response.id;
            this.authId = this.auth.id;
            return response;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async attachDocument(document: UM.AuthDocument): Promise<IApiActionResults> {
        if(this.documentsConfig && this.documentsConfig.addTargetProperty) {
            this.documentsConfig.addTargetProperty(document);
        }
        try {
            this.state.beginSave();
            return await this.documentService.attachDocument(document);
        }
        catch(error) {
            this.toaster.standard.saved('core:document.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }


    ////****************** IForm ****************************************************////
    public reset(): void {
        this.authCreated = false;
        this.memberPlan = null;
        this.auth = {};
        this.overrideRequestedDate = false;
    }
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }
    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        if(!this.auth || !this.memberPlan || this.state.busy || this.authCreated) return false;

        try {
            this.state.beginProcessing();

            const metadata: UM.AuthMetadata | undefined = !this.taskId ? undefined : {
                created: {
                    byTask: { id: this.taskId }
                }
            };

            const newAuth: UM.Auth = {
                class: getReference(this.auth.class, {
                    subClass: getReference(this.auth.class && this.auth.class.subClass)
                }),
                type: getReference(this.auth.type),
                priority: getReference(this.auth.priority),
                requestedBy: { id: this.env.user.id },
                assignedTo: getReference(this.auth.assignedTo),
                initialDateOfService: this.auth.initialDateOfService,
                metadata
            }

            if(this.faxDocument) {
                newAuth.requestedDateTime = this.faxDocument.receivedTimestamp;
            }

            if(this.overrideRequestedDate) {
                newAuth.overrideRequestedDateTime = this.auth.overrideRequestedDateTime;

                if(!newAuth.metadata) {
                    newAuth.metadata = {};
                }

                newAuth.metadata.requestedOverride = {
                    reason: getReference(this.auth.metadata?.requestedOverride?.reason)
                }
            }

            if(this.auth.alternateNumber) {
                newAuth.alternateNumber = this.auth.alternateNumber;
            }

            newAuth.serviceDate = this.auth.serviceDate;

            let date;
            let source;

            if(!this.auth.type?.flag?.usesPreserviceDay && this.auth.class?.subClass?.isAdmission) {
                date = this.auth.serviceDate?.dateRange?.start
            }

            if(this.auth.admission) {
                source = getReference(this.auth.source)
            }

            if(date || source) {
                newAuth.admission = { date, source };
            }

            if(this.memberPlan.id) {
                newAuth.member = {
                    plan: getReference(this.memberPlan),
                };
            }
            else if(this.memberPlan && this.memberPlan.identifier && this.memberPlan.identifier.primary && this.memberPlan.member && this.memberPlan.member.name) {
                newAuth.member = {
                    name: {
                        full: this.memberPlan.member.name.full
                    },
                    primaryPlan: {
                        identifier: {
                            primary: this.memberPlan.identifier.primary
                        }
                    },
                    isUnknown: true,
                }
            }
            if(this.faxDocument) {
                newAuth.source = { code: 'FAX' };
            }

            const response = await this.createAuth(newAuth);
            this.authCreated = response.isSuccessful;

            // If faxDocument is not null, prepare document to attach to the new Auth
            if(this.faxDocument) {
                const attachDocument: UM.AuthDocument = {
                    id: this.faxDocument.document?.id
                };

                await this.attachDocument(attachDocument);
            }

            return this.authId != null;
        }
        finally {
            this.state.endProcessing();
        }
    }
}
